import { ConfirmationDialog } from 'components/ConfirmationDialog';
import { EditRatioForm } from 'components/EditRatioForm';
import { ModalDialog } from 'components/ModalDialog';
import { RequestStatus } from 'features/request/requestSlice';
import { useRequest } from 'features/request/useRequest';
import { useResetStoreData } from 'features/request/useResetStoreData';
import type { WorkOrderChangePriceData, WorkOrderChangePriceItem } from 'gql/graphql';
import { RatioTypeEnum } from 'gql/graphql';
import { useProjectData } from 'hooks/useProjectData';
import { priceListRatioRowGql } from 'pages/Project/UerProject/Ratio/CreateOrEditRatio/createOrEditRatioQueries';
import { useIsUniqueRatioWorkConditions } from 'pages/Project/UerProject/WorkOrder/hooks/useIsUniqueRatioWorkConditions';
import { coefficientsItems } from 'pages/Project/UerProject/WorkOrder/workOrderConstants';
import { requiredBaseRule } from 'utils/constants';
import { replacerCost } from 'utils/replacerCost';

import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useForm, useWatch } from 'react-hook-form';
import { useDispatch } from 'react-redux';
import { useParams } from 'react-router-dom';

import { updateWorkOrderChangeRatio } from '../WorkOrderChangeQueries';

const techPartValidator = {
    validate: (v: any[]) => (!v?.length ? requiredBaseRule.required : undefined),
};

const constraintCoefValidator = {
    validate: (v: any[]) => (!v ? requiredBaseRule.required : undefined),
};
const workConditionsKey = 'workConditions';

export const EditWorkOrderChangeRatio = ({
    isModalOpened,
    closeModalHandler,
    ratioId,
    checkedUerRow,
    refreshTable,
}: {
    isModalOpened: boolean;
    closeModalHandler: () => void;
    ratioId: number;
    // TODO: поправить типы после перехода на новый изм
    checkedUerRow: WorkOrderChangePriceData | WorkOrderChangePriceItem;
    refreshTable: (objectIds: number[]) => void;
}) => {
    const dispatch = useDispatch();
    const { id: projectId } = useParams();
    const { projectData } = useProjectData(projectId);

    const {
        control,
        reset,
        setValue,
        formState: { isDirty, errors },
        handleSubmit,
        setError,
        clearErrors,
    } = useForm({});
    const [selectedCoefficientType, setSelectedCoefficientType] = useState(coefficientsItems[0]);
    const [isOpenWarningDialog, setIsOpenWarningDialog] = useState<boolean>(false);

    const workConditions: string = useWatch({ control, name: workConditionsKey });

    const { result: uniqueResult, message: uniqueMessage } = useIsUniqueRatioWorkConditions({
        contractId: projectData?.contractId,
        ratioRowId: ratioId ? Number(ratioId) : null,
        workConditions,
    });

    useEffect(() => {
        if (workConditions && !uniqueResult) {
            setError(workConditionsKey, {
                message: uniqueMessage as string,
            });
        } else {
            clearErrors(workConditionsKey);
        }
    }, [clearErrors, workConditions, setError, uniqueResult, uniqueMessage]);

    const ratioRowDataVariables = useMemo(
        () => ({
            ratioRowId: Number(ratioId),
        }),
        [ratioId],
    );

    const [ratioData, getRatioDataStatus] = useRequest({
        requestId: 'workOrderChangeRatioData',
        query: priceListRatioRowGql,
        variables: ratioRowDataVariables,
        withEffect: !!ratioId,
    });

    useEffect(() => {
        if (ratioData?.uerPriceListRatioRow?.data) {
            reset(ratioData?.uerPriceListRatioRow?.data);

            setValue(
                'appliesToUerItems',
                ratioData?.uerPriceListRatioRow?.data?.appliesTo?.map((item) => ({
                    id: item.appliesToId,
                    code: item.appliesToCode,
                })),
            );
            setValue(
                'appliesToUerIds',
                ratioData?.uerPriceListRatioRow?.data?.appliesTo?.map((item) => item.appliesToId),
            );
        }
    }, [ratioData?.uerPriceListRatioRow?.data, reset, setValue]);

    const [resetRatioRowData] = useResetStoreData({ requestId: 'workOrderChangeRatioData' });
    const [resetUpdateResultData] = useResetStoreData({ requestId: 'updateWorkOrderChangeRatio' });

    const resetAllEditModalData = useCallback(() => {
        resetRatioRowData(undefined);
        resetUpdateResultData(undefined);
    }, [resetRatioRowData, resetUpdateResultData]);

    useEffect(
        () => () => {
            resetAllEditModalData();
        },
        [resetAllEditModalData],
    );

    const isConstraintCoef = selectedCoefficientType?.id === RatioTypeEnum.ConstraintCoef;

    const ratioType = useMemo(
        () => ratioData?.uerPriceListRatioRow?.data?.ratioType,
        [ratioData?.uerPriceListRatioRow?.data?.ratioType],
    ) as RatioTypeEnum;

    const editingRatioStatus = useMemo(
        () => ratioData?.uerPriceListRatioRow?.data?.status,
        [ratioData?.uerPriceListRatioRow?.data?.status],
    );

    const appliesToUerIdsControl = useMemo(
        () => ({
            control,
            name: 'appliesToUerIds',
            rules: ratioType === RatioTypeEnum.TechPartCoef ? techPartValidator : undefined,
        }),
        [ratioType, control],
    );

    const ratioGroupIdControl = useMemo(
        () => ({
            control,
            name: 'ratioGroupId',
            rules: ratioType === RatioTypeEnum.ConstraintCoef ? constraintCoefValidator : undefined,
        }),
        [ratioType, control],
    );

    const groupTypeControl = useMemo(
        () => ({
            control,
            name: 'groupType',
            rules: ratioType === RatioTypeEnum.DismantlingCoef ? constraintCoefValidator : undefined,
        }),
        [control, ratioType],
    );

    useEffect(() => {
        setSelectedCoefficientType(() => {
            if (ratioType === RatioTypeEnum.ConstraintCoef) {
                return coefficientsItems[0];
            }

            if (ratioType === RatioTypeEnum.TechPartCoef) {
                return coefficientsItems[1];
            }

            return coefficientsItems[2];
        });
    }, [ratioType]);

    const applicationSelectorDisabledIds = useMemo(() => {
        const usedIds = (ratioData?.uerPriceListRatioRow?.data?.appliesTo || [])
            .filter((item) => item.isUsedLink)
            .map((item) => item.appliesToId);

        return !isConstraintCoef && checkedUerRow ? [checkedUerRow.uerId, ...usedIds] : usedIds;
    }, [checkedUerRow, isConstraintCoef, ratioData?.uerPriceListRatioRow?.data?.appliesTo]);

    const ratioGroup = useMemo(() => {
        if (ratioId && ratioData?.uerPriceListRatioRow?.data?.groupId) {
            return {
                sectionId: ratioData?.uerPriceListRatioRow?.data?.groupId,
                sectionName: ratioData?.uerPriceListRatioRow?.data?.groupName,
            };
        }

        return undefined;
    }, [ratioData?.uerPriceListRatioRow?.data?.groupId, ratioData?.uerPriceListRatioRow?.data?.groupName, ratioId]);

    useEffect(() => {
        setValue('ratioGroup', ratioGroup);
        setValue('ratioGroupId', ratioGroup?.sectionId);
    }, [ratioGroup, setValue]);

    useEffect(() => {
        setValue('appliesToUerItems', checkedUerRow ? [{ id: checkedUerRow?.uerId, code: checkedUerRow?.code }] : []);
        setValue('appliesToUerIds', [checkedUerRow?.uerId]);
    }, [setValue, checkedUerRow]);

    const isApplicationAreaDisabled = useMemo(
        () => isConstraintCoef && (!!ratioId || !!ratioGroup),
        [ratioId, isConstraintCoef, ratioGroup],
    );

    const handleSubmitWarningDialog = useCallback(() => {
        closeModalHandler();
    }, [closeModalHandler]);

    const handleCancelWarningDialog = useCallback(() => {
        setIsOpenWarningDialog(false);
    }, []);

    const onCancel = useCallback(() => {
        if (isDirty) {
            setIsOpenWarningDialog(true);
        } else {
            closeModalHandler();
        }
    }, [closeModalHandler, isDirty]);

    const objectIdsArrEditMutation = useMemo(
        () => [checkedUerRow?.workOrderObjectId],
        [checkedUerRow?.workOrderObjectId],
    );

    const successUpdateHandler = useCallback(() => {
        refreshTable?.(objectIdsArrEditMutation);
        closeModalHandler?.();
        resetAllEditModalData();
    }, [closeModalHandler, objectIdsArrEditMutation, refreshTable, resetAllEditModalData]);

    const [, updateRatioStatus, , updateRatioRequest] = useRequest({
        requestId: 'updateWorkOrderChangeRatio',
        query: updateWorkOrderChangeRatio,
        withEffect: false,
        dependenciesUpdater: successUpdateHandler,
    });

    const editHandler = useCallback(
        (submitData) => {
            const variables = {
                workOrderChangePriceId: checkedUerRow?.workOrderChangePriceId,
                input: {
                    ratioRowId: submitData.id,
                    contractId: projectData?.contractId,
                    ratioType,
                    appliesToUerRowIds: submitData.appliesToUerIds,
                    kem: replacerCost(submitData.kem),
                    kmat: replacerCost(submitData.kmat),
                    knr: replacerCost(submitData.knr),
                    kozp: replacerCost(submitData.kozp),
                    ksp: replacerCost(submitData.ksp),
                    ktz: replacerCost(submitData.ktz),
                    ktzzpm: replacerCost(submitData.ktzzpm),
                    justification: submitData.justification,
                    workConditions: submitData.workConditions,
                    ratioGroupId: submitData.ratioGroupId,
                    targetUerId: checkedUerRow.uerId,
                },
            };

            dispatch(updateRatioRequest(variables));
        },
        [checkedUerRow, dispatch, projectData?.contractId, ratioType, updateRatioRequest],
    );

    return (
        <ModalDialog
            isOpen={isModalOpened}
            title="Редактирование коэффициента"
            hasOverlay
            onClose={onCancel}
            onConfirm={handleSubmit(editHandler)}
            style={{ width: 600 }}
            withTitleCloseButton
            isCommit={updateRatioStatus === RequestStatus.FETCHING}
            btnConfirmDisabled={!isDirty || !!Object.keys(errors || {}).length}
            isOnClickOutsideDisable
        >
            <EditRatioForm
                isDataFetching={getRatioDataStatus === RequestStatus.FETCHING}
                control={control}
                isCustom={!!ratioData?.uerPriceListRatioRow?.data?.isCustom}
                ratioId={ratioId}
                selectedCoefficientType={selectedCoefficientType}
                ratioType={ratioType}
                appliesToUerIdsControl={appliesToUerIdsControl}
                ratioGroupIdControl={ratioGroupIdControl}
                isApplicationAreaDisabled={isApplicationAreaDisabled}
                applicationSelectorDisabledIds={applicationSelectorDisabledIds}
                groupTypeControl={groupTypeControl}
                contractId={projectData?.contractId}
                uerId={checkedUerRow.uerId}
                editingRatioStatus={editingRatioStatus}
            />
            <ConfirmationDialog
                textHeader="ОТМЕНА РЕДАКТИРОВАНИЯ КОЭФФИЦИЕНТА"
                textConfirmation="Вы уверены, что хотите отменить? Внесённые изменения не будут сохранены"
                cancelButtonText="Не отменять"
                confirmButtonText="Отменить"
                isOpened={isOpenWarningDialog}
                onSubmit={handleSubmitWarningDialog}
                onClose={handleCancelWarningDialog}
            />
        </ModalDialog>
    );
};

EditWorkOrderChangeRatio.displayName = 'EditWorkOrderChangeRatio';
